import type { MessageDescriptor } from 'react-intl';

import { Action } from '~/eds/src/types/Action';
import { useCanEditEntityGroups } from '~/hooks/useCanEditEntityGroups';

/**
 * Config for one docked toolbar panel consumed by `AppShell`. The rail renders
 * `action` as an icon button (via the EDS 2.0 `Toolbar`); `component` is the
 * panel body shown beside the rail when the action is active. `width` seeds the
 * panel's initial docked width (`s`/`m`), or `fill` to size from content.
 */
type PanelWidth = 's' | 'm' | 'fill';

export interface ToolbarPanel<V> {
  action: Action<V>;
  component: React.ReactNode;
  width?: PanelWidth;
}

/**
 * Builds one docked toolbar panel entry. Shared by the document page and
 * docked-rail toolbar-panel builders, which both surface the same
 * fields/clauses/matches/groups actions but render `component` differently.
 */
export function buildToolbarPanel<V>(
  value: V,
  icon: Action<V>['icon'],
  label: string,
  tooltip: string,
  component: React.ReactNode,
): ToolbarPanel<V> {
  return {
    action: { icon, label, tooltip, value },
    component,
  };
}

/**
 * Docked panel keys for the shared details rail. A lookup rather than loose
 * literals so callers outside the rail can name one (`DETAILS_PANEL_TABS.matches`)
 * without repeating the string, and so the tab union below cannot drift from the
 * keys the rail actually builds.
 */
export const DETAILS_PANEL_TABS = {
  fields: 'fields',
  clauses: 'clauses',
  groups: 'groups',
  matches: 'matches',
} as const;

export type DetailsPanelTab =
  (typeof DETAILS_PANEL_TABS)[keyof typeof DETAILS_PANEL_TABS];

export interface DetailsPanelMessages {
  fieldsLabel: MessageDescriptor;
  fieldsTooltip: MessageDescriptor;
  clausesLabel: MessageDescriptor;
  clausesTooltip: MessageDescriptor;
  groupsLabel: MessageDescriptor;
  groupsTooltip: MessageDescriptor;
  matchesLabel: MessageDescriptor;
  matchesTooltip: MessageDescriptor;
}

/**
 * Builds the fields/clauses/groups/matches docked toolbar panels shared by the
 * document page and docked-rail builders. Both surfaces show the same
 * actions with the same icons; `renderPanel` supplies the one thing that
 * actually differs between them — how each surface renders its details
 * component for a given tab. `messages` stays per-caller so each surface
 * keeps its own translation IDs.
 */
export function useBuildDetailsPanels(
  formatMessage: (descriptor: MessageDescriptor) => string,
  messages: DetailsPanelMessages,
  renderPanel: (singleTab: DetailsPanelTab) => React.ReactNode,
  includeMatches: boolean,
): Record<string, ToolbarPanel<string>> {
  const { canView: canViewEntityGroups } = useCanEditEntityGroups();
  const panels: Record<string, ToolbarPanel<string>> = {
    [DETAILS_PANEL_TABS.fields]: buildToolbarPanel<string>(
      DETAILS_PANEL_TABS.fields,
      'field',
      formatMessage(messages.fieldsLabel),
      formatMessage(messages.fieldsTooltip),
      renderPanel(DETAILS_PANEL_TABS.fields),
    ),
    [DETAILS_PANEL_TABS.clauses]: buildToolbarPanel<string>(
      DETAILS_PANEL_TABS.clauses,
      'field-clause',
      formatMessage(messages.clausesLabel),
      formatMessage(messages.clausesTooltip),
      renderPanel(DETAILS_PANEL_TABS.clauses),
    ),
  };
  if (includeMatches) {
    panels[DETAILS_PANEL_TABS.matches] = buildToolbarPanel<string>(
      DETAILS_PANEL_TABS.matches,
      'search',
      formatMessage(messages.matchesLabel),
      formatMessage(messages.matchesTooltip),
      renderPanel(DETAILS_PANEL_TABS.matches),
    );
  }
  if (canViewEntityGroups) {
    panels[DETAILS_PANEL_TABS.groups] = buildToolbarPanel<string>(
      DETAILS_PANEL_TABS.groups,
      'groups',
      formatMessage(messages.groupsLabel),
      formatMessage(messages.groupsTooltip),
      renderPanel(DETAILS_PANEL_TABS.groups),
    );
  }
  return panels;
}
